import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { type InterviewPanelMemberData } from './types';
import { PanelInterviewBox } from './PanelInterviewBox';
import {
  Alert,
  type AlertDetails,
  Loading,
} from '../../../../../../components';
import {
  useGetInterviewPanel,
  useUpdateInterviewPanelMember,
} from '../../../../graphql';

type PanelInterviewProps = {
  isViewMode?: boolean;
  hideDeclaration?: boolean;
  onJumpToNext?: () => void;
  refetchAssessment: () => void;
};

export const PanelInterview: React.FunctionComponent<PanelInterviewProps> = (
  props: PanelInterviewProps,
) => {
  const {
    isViewMode = false,
    hideDeclaration = false,
    onJumpToNext = () => {},
    refetchAssessment,
  } = props;
  const { applicationId = '' } = useParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const {
    interviewPanelListData: initialInterviewPanelListData,
    loading,
    error,
    refetch,
  } = useGetInterviewPanel(applicationId);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: 'Error Occurs.' + (error as Error)?.message,
          alertType: 'warning',
        }}
      />
    );
  }

  const updateInterviewPanelMember = useUpdateInterviewPanelMember();
  const handleUpdateInterviewPanelMember = async (
    interviewPanelListData: InterviewPanelMemberData[],
  ): Promise<void> => {
    setSaveLoading(true);
    try {
      const variables = {
        input: {
          applicationId,
          panelMembers: interviewPanelListData,
        },
      };

      await updateInterviewPanelMember({
        variables,
      });
      setAlertDetails({
        alertContent: 'Saved Interview Panel Member List Successfully',
        alertType: 'success',
      });
      refetch();
      refetchAssessment();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setAlertDetails({
        alertContent:
          'Saved Interview Panel Member List Failed' +
          (error as Error)?.message,
        alertType: 'error',
      });
    } finally {
      setSaveLoading(false);
      setOpenAlert(true);
    }
  };

  const defaultPanelMember: InterviewPanelMemberData = {
    netId: '',
    title: '',
    jobTitle: '',
    givenName: '',
    surname: '',
    institution: '',
    department: '',
    email: '',
    isPanelChair: false,
    post: '',
    rank: '',
  };

  const interviewPanelListData: InterviewPanelMemberData[] =
    initialInterviewPanelListData?.length === 0
      ? [
          { ...defaultPanelMember, isPanelChair: true },
          { ...defaultPanelMember, isPanelChair: false },
        ]
      : initialInterviewPanelListData?.length === 1
        ? [
            ...initialInterviewPanelListData,
            { ...defaultPanelMember, isPanelChair: false },
          ]
        : initialInterviewPanelListData;

  return loading ? (
    <Loading />
  ) : (
    <div className="!p-0">
      {saveLoading && <Loading />}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <PanelInterviewBox
        hideDeclaration={hideDeclaration}
        initialCheckbox={Boolean(initialInterviewPanelListData?.length)}
        initialValues={interviewPanelListData}
        handleSubmit={handleUpdateInterviewPanelMember}
        refetchAssessment={refetch}
        onJumpToNext={onJumpToNext}
        isViewMode={isViewMode}
      />
    </div>
  );
};
